// A customer's tags as chips (spec 09 §4.1 and §4.2, D-323 ruling 5). Every
// tag renders as the NEUTRAL chip: a tag has no colour (§2.27, amendment
// (36)), so the name carries the meaning, and a colour the tenant never chose
// would read as a status. An archived tag still shows on the customers that
// carry it (it keeps its links) and says so in a quiet suffix, so a reader
// does not go looking for it in a picker that no longer offers it.
//
// The chips come in the wire's order, the lower-cased name and then the id,
// so two screens showing one customer's tags show them alike.

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { StatusChip } from "../../../../../components/status-chip";
import type { CustomerTagRef } from "../../hooks/crm-queries";

export function TagChips({
  tags,
  "data-testid": testId = "sales-tag-chips",
  action,
}: {
  tags: readonly CustomerTagRef[];
  "data-testid"?: string;
  /** A control rendered after one chip, inside its list item: the 360's
   * remove button for a writer. */
  action?: ((tag: CustomerTagRef) => ReactNode) | undefined;
}) {
  const { t } = useTranslation();
  if (tags.length === 0) return null;
  return (
    <ul className="sales-tag-chips" data-testid={testId}>
      {tags.map((tag) => (
        <li key={tag.id} className="sales-tag-item" data-tag-id={tag.id}>
          <StatusChip
            variant="neutral"
            dot={false}
            className="sales-tag-chip"
            data-testid="sales-tag-chip"
            data-status={tag.status}
          >
            {/* One inline run holds the name and the suffix, so the space
                between them is real text: the chip is a flex box, which
                drops a whitespace node between two of its items, and a read
                of the chip would otherwise give "VIParchived". */}
            <span className="sales-tag-name">
              {tag.name}
              {tag.status === "archived" ? (
                <>
                  {" "}
                  <span className="sales-tag-archived">{t("sales.tags.archivedSuffix")}</span>
                </>
              ) : null}
            </span>
          </StatusChip>
          {action?.(tag)}
        </li>
      ))}
    </ul>
  );
}
